import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../api/axios';

const SPECIALTY_ICONS = {
  stethoscope:'🩺', baby:'👶', female:'👩', heart:'❤️',
  hand:'✋', eye:'👁️', tooth:'🦷', bone:'🦴',
  brain:'🧠', kidneys:'🫘', ear:'👂', neurology:'🧬',
  maternity:'🤰', leaf:'🌿',
};

export default function DoctorsList() {
  const [doctors, setDoctors] = useState([]);
  const [specialties, setSpecialties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ specialty_id:'', city:'', search:'' });
  const navigate = useNavigate();

  const cities = ['Lomé','Kpalimé','Atakpamé','Sokodé','Kara','Dapaong','Tsévié','Aného'];

  useEffect(() => {
    api.get('/specialties').then(r => setSpecialties(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (filters.specialty_id) params.append('specialty_id', filters.specialty_id);
    if (filters.city) params.append('city', filters.city);
    if (filters.search) params.append('search', filters.search);
    api.get('/doctors?' + params.toString())
      .then(r => setDoctors(r.data.data || []))
      .catch(() => setDoctors([]))
      .finally(() => setLoading(false));
  }, [filters]);

  const selectSpecialty = (id) => {
    setFilters(f => ({...f, specialty_id: f.specialty_id === String(id) ? '' : String(id)}));
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b px-4 py-3 flex items-center gap-3 sticky top-0 z-10">
        <button onClick={() => navigate(-1)} className="text-green-600 font-medium">← Retour</button>
        <h1 className="font-bold text-gray-800">Trouver un médecin</h1>
      </div>

      <div className="max-w-lg mx-auto p-4 space-y-4">
        {/* Barre de recherche */}
        <input className="input-field" placeholder="🔍 Rechercher un médecin..."
          value={filters.search} onChange={e => setFilters({...filters, search: e.target.value})} />

        {/* Spécialités cliquables */}
        <div>
          <p className="text-xs font-medium text-gray-500 mb-2 uppercase tracking-wide">Spécialités</p>
          <div className="flex gap-2 overflow-x-auto pb-2">
            <button
              onClick={() => setFilters(f => ({...f, specialty_id: ''}))}
              className={`flex-shrink-0 px-3 py-2 rounded-xl text-xs font-medium border-2 transition-all ${filters.specialty_id === '' ? 'border-green-500 bg-green-50 text-green-700' : 'border-gray-200 bg-white text-gray-600'}`}>
              Toutes
            </button>
            {specialties.map(s => (
              <button key={s.id}
                onClick={() => selectSpecialty(s.id)}
                className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium border-2 transition-all ${filters.specialty_id === String(s.id) ? 'border-green-500 bg-green-50 text-green-700' : 'border-gray-200 bg-white text-gray-600'}`}>
                <span>{SPECIALTY_ICONS[s.icon] || '🏥'}</span>
                <span>{s.name_fr}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Filtre ville */}
        <select className="input-field text-sm" value={filters.city}
          onChange={e => setFilters({...filters, city: e.target.value})}>
          <option value="">📍 Toutes les villes</option>
          {cities.map(c => <option key={c} value={c}>{c}</option>)}
        </select>

        {/* Résultats */}
        {loading ? (
          <div className="text-center py-10">
            <div className="text-3xl animate-pulse mb-2">🔍</div>
            <p className="text-gray-400">Recherche en cours...</p>
          </div>
        ) : doctors.length === 0 ? (
          <div className="text-center py-10">
            <p className="text-4xl mb-3">😔</p>
            <p className="text-gray-500">Aucun médecin trouvé</p>
            <p className="text-sm text-gray-400 mt-1">Essayez d'autres critères</p>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-xs text-gray-500">{doctors.length} médecin(s) trouvé(s)</p>
            {doctors.map(doc => (
              <Link key={doc.id} to={'/doctors/' + doc.id}
                className="card flex items-start gap-3 hover:border-green-300 hover:shadow-md transition-all block">
                <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center text-2xl flex-shrink-0">
                  {doc.user?.gender === 'female' ? '👩‍⚕️' : '👨‍⚕️'}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-gray-800">{doc.user?.name}</p>
                  <p className="text-sm text-green-600 font-medium">{doc.specialty?.name_fr}</p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    📍 {doc.city} {doc.clinic_name ? '• ' + doc.clinic_name : ''}
                  </p>
                  <div className="flex items-center justify-between mt-1.5">
                    <div className="flex gap-1">
                      {doc.accepts_mobile_money && <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full">📱</span>}
                      {doc.teleconsultation_available && <span className="text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded-full">📹</span>}
                      {doc.home_visit_available && <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full">🏠</span>}
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold text-green-700">{parseInt(doc.consultation_fee).toLocaleString()} FCFA</p>
                      {doc.rating > 0 && <p className="text-xs text-gray-400">⭐ {doc.rating}</p>}
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
